import React, { FunctionComponent, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputAddress, InputAddressData, InputOnFocus } from '@afterpaytouch/core'
import { AddressFieldSchema } from '../../types'
import { useVisible, useError } from '../../utils/hooks'

const addressFields = ['address1', 'address2', 'area2', 'suburb', 'state', 'postcode', 'countryCode']

export const AddressField: FunctionComponent<AddressFieldSchema> = ({
  name = '',
  props,
  options,
  apiKey,
  show = true,
  validateOnLoad = false,
  testNameSpace = '',
}) => {
  const { register, setValue, getValues, resetField, trigger, setError } = useFormContext()
  const { isVisible, values } = useVisible({ name, show })
  const { isError, errorText } = useError({ name, error: props?.error, errorText: props?.errorText })

  const defaultAddressValues =
    typeof props?.initialAddress !== 'undefined' && Array.isArray(props?.initialAddress) ? props.initialAddress[0] : props?.initialAddress

  const component = register(name, {
    ...options,
    value: values ?? defaultAddressValues,
  })

  addressFields.forEach((childName) => register(`${name}.${childName}`))

  const onAddressChange = (address: InputAddressData, manuallyTouched: boolean | undefined, validity: boolean | undefined): void => {
    setValue(name, address, { shouldTouch: manuallyTouched, shouldDirty: manuallyTouched })
  }

  const onInputValueBlur = (e: InputOnFocus): void => {
    if (typeof getValues(name)?.address1 === 'undefined') {
      setValue(name, undefined, { shouldValidate: true })
    }
    component.onBlur(e)
    if (typeof props?.blurFieldDependencies !== 'undefined') {
      trigger(props.blurFieldDependencies)
    }
  }

  const onAddressBlur = (validity: boolean | undefined): void => {
    trigger(name)
  }

  // To be used for internal library errors for e.g Google Places API
  const onManualErrorTrigger = (): void => {
    setError(component.name, {})
  }

  useEffect(() => {
    if (validateOnLoad) {
      trigger(name)
    }
  }, [])

  useEffect(() => {
    resetField(name, { defaultValue: { ...defaultAddressValues, countryCode: props?.componentRestrictions?.country } })
  }, [props?.key])

  return isVisible ? (
    <InputAddress
      {...props}
      name={component.name}
      onAddressChange={onAddressChange}
      onInputValueBlur={onInputValueBlur}
      onAddressBlur={onAddressBlur}
      testNameSpace={testNameSpace}
      apiKey={apiKey}
      onManualErrorTrigger={onManualErrorTrigger}
      error={isError}
      errorText={errorText}
      showAutoCompeleteErrorTextOnly={false}
    />
  ) : null
}
